import type { CSSProperties } from 'react';

import { Button } from '@/components/ui/button';
import { hasActiveToasts, useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const MAX_CHANGELOG_ITEMS = 5;
const TOAST_DECK_UPDATE_OFFSET = 'calc(1.25rem + var(--jointo-toast-deck-height, 96px) + 0.75rem)';

interface AppUpdateToastProps {
  open: boolean;
  remoteVersion: string | undefined;
  changelog: string[];
  onLater: () => void;
  onNow: () => Promise<void>;
}

export function AppUpdateToast({
  open,
  remoteVersion,
  changelog,
  onLater,
  onNow,
}: AppUpdateToastProps) {
  const { toasts } = useToast();
  const shouldLiftAboveToasts = hasActiveToasts(toasts);

  if (!open) return null;

  const displayChangelog = changelog.slice(0, MAX_CHANGELOG_ITEMS);
  const style: CSSProperties | undefined = shouldLiftAboveToasts
    ? { bottom: TOAST_DECK_UPDATE_OFFSET }
    : undefined;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed right-5 w-80 rounded-2xl border border-border-base/30 bg-bg-element/90 shadow-lg backdrop-blur-md transition-[bottom] duration-200',
        shouldLiftAboveToasts ? 'z-[110]' : 'bottom-5 z-50'
      )}
      style={style}
    >
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-text-primary">发现新版本</p>
            {remoteVersion && (
              <p className="text-xs text-text-secondary mt-0.5">v{remoteVersion} 已可用</p>
            )}
          </div>
          <span className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-success animate-pulse" />
        </div>

        {displayChangelog.length > 0 && (
          <ul className="rounded-xl border border-border-base/20 bg-fill-lighter/20 px-3 py-2.5 space-y-1">
            {displayChangelog.map((item, index) => (
              <li key={index} className="flex items-start gap-1.5 text-xs text-text-secondary">
                <span className="mt-0.5 flex-shrink-0 text-text-tertiary">·</span>
                <span>{item}</span>
              </li>
            ))}
            {changelog.length > MAX_CHANGELOG_ITEMS && (
              <li className="text-xs text-text-tertiary pl-3.5">
                以及其他 {changelog.length - MAX_CHANGELOG_ITEMS} 项改动
              </li>
            )}
          </ul>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="highlightSubtle" size="sm" onClick={onLater}>
            稍后
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              void onNow();
            }}
          >
            立即更新
          </Button>
        </div>
      </div>
    </div>
  );
}
